import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { DEFAULT_STALE_TIME } from '~/constants'
import { TAgent } from '~/features/agents/models'
import { queryKeys } from '~/features/agents/utils'
import useHasValidAuthSession from '~/features/auth/hooks/useHasValidAuthSession'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import { HttpServiceBaseResponse } from '~/services/http/models'

type TParams = {
  orgId: string
  queryOptions?: Partial<UseQueryOptions<HttpServiceBaseResponse<TAgent[]>>>
}

const useGetAgentsByOrgId = ({ orgId, queryOptions }: TParams) => {
  const { hasValidAuthSession, isLoggedIn } = useHasValidAuthSession()

  const queryFn = useCallback(
    () =>
      HttpService.get<TAgent[]>(
        {
          path: EServerPath.agents,
          query: { orgId },
        },
        EHttpService.agent,
      ),
    [orgId],
  )

  return useQuery({
    queryKey: queryKeys.agentsByOrganization(orgId),
    queryFn,
    retry: false,
    retryOnMount: false,
    staleTime: DEFAULT_STALE_TIME,
    // A signed-in user waits for the grouped token so we don't fire against
    // this auth-protected endpoint before it's ready; guests have no orgId.
    enabled: !!orgId && (!isLoggedIn || hasValidAuthSession),
    ...queryOptions,
  })
}

export default useGetAgentsByOrgId
